import { useState, useEffect, useRef } from "react";
import {
  HelpCircle,
  X,
  ChevronDown,
  MousePointerClick,
  Layers,
  Search,
  ShoppingCart,
  Keyboard,
  Lightbulb,
  BookOpen,
} from "lucide-react";
import { OnboardingTour, StartTourButton, isTourCompleted } from "./OnboardingTour";
import { useAuthSafe } from "./AuthContext";

/* ================================================================
   HelpDrawer — floating ? button (bottom-RIGHT) + slide-out help
   panel + onboarding tour.

   Auto-starts the tour on first visit (if not completed before).
   ================================================================ */

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* ---------- Help content data ---------- */

interface HelpSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  items: { q: string; a: string }[];
}

const HELP_SECTIONS: HelpSection[] = [
  {
    id: "basics",
    icon: <Lightbulb className="w-4 h-4" />,
    title: "\u05D9\u05E1\u05D5\u05D3\u05D5\u05EA",
    items: [
      {
        q: "\u05D0\u05D9\u05DA \u05E0\u05DB\u05E0\u05E1\u05D9\u05DD \u05DC\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4?",
        a: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05E2\u05D9\u05E4\u05E8\u05D5\u05DF (\u05E4\u05D9\u05E0\u05D4 \u05E9\u05DE\u05D0\u05DC\u05D9\u05EA \u05EA\u05D7\u05EA\u05D5\u05E0\u05D4) \u05D0\u05D5 \u05D4\u05E7\u05D9\u05E9\u05D5 Ctrl+E. \u05E1\u05E8\u05D2\u05DC \u05E2\u05E8\u05D9\u05DB\u05D4 \u05DB\u05EA\u05D5\u05DD \u05D9\u05D5\u05E4\u05D9\u05E2 \u05D1\u05E8\u05D0\u05E9 \u05D4\u05DE\u05E1\u05DA.",
      },
      {
        q: "\u05D0\u05D9\u05DA \u05E9\u05D5\u05DE\u05E8\u05D9\u05DD \u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD?",
        a: "\u05D4\u05DB\u05DC \u05E0\u05E9\u05DE\u05E8 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA! \u05D1\u05E8\u05D2\u05E2 \u05E9\u05EA\u05E2\u05E8\u05DB\u05D5 \u05D8\u05E7\u05E1\u05D8 \u05D0\u05D5 \u05EA\u05D2\u05E8\u05E8\u05D5 \u05D0\u05DC\u05DE\u05E0\u05D8, \u05D4\u05E9\u05D9\u05E0\u05D5\u05D9 \u05E0\u05E9\u05DE\u05E8 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05DC\u05E9\u05E8\u05EA. \u05E1\u05D8\u05D5\u05E1 \u05D4\u05E9\u05DE\u05D9\u05E8\u05D4 \u05DE\u05D5\u05E4\u05D9\u05E2 \u05D1\u05E1\u05E8\u05D2\u05DC.",
      },
      {
        q: "\u05D0\u05D9\u05DA \u05DE\u05D1\u05D8\u05DC\u05D9\u05DD \u05E9\u05D9\u05E0\u05D5\u05D9?",
        a: "\u05D4\u05E9\u05EA\u05DE\u05E9\u05D5 \u05D1\u05DB\u05E4\u05EA\u05D5\u05E8\u05D9 \u05D1\u05D9\u05D8\u05D5\u05DC/\u05E9\u05D7\u05D6\u05D5\u05E8 \u05D1\u05E1\u05E8\u05D2\u05DC \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4, \u05D0\u05D5 Ctrl+Z \u05DC\u05D1\u05D9\u05D8\u05D5\u05DC \u05D5-Ctrl+Y \u05DC\u05E9\u05D7\u05D6\u05D5\u05E8.",
      },
    ],
  },
  {
    id: "content",
    icon: <MousePointerClick className="w-4 h-4" />,
    title: "\u05E2\u05E8\u05D9\u05DB\u05EA \u05EA\u05D5\u05DB\u05DF",
    items: [
      {
        q: "\u05E2\u05E8\u05D9\u05DB\u05EA \u05D8\u05E7\u05E1\u05D8 \u05D9\u05E9\u05D9\u05E8\u05D4",
        a: "\u05D1\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4, \u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05DC \u05DB\u05D5\u05EA\u05E8\u05EA, \u05E4\u05E1\u05E7\u05D4 \u05D0\u05D5 \u05D8\u05E7\u05E1\u05D8 \u05D0\u05D7\u05E8 \u2014 \u05D4\u05D5\u05D0 \u05D9\u05D4\u05E4\u05D5\u05DA \u05DC\u05E9\u05D3\u05D4 \u05E2\u05E8\u05D9\u05DB\u05D4 \u05E2\u05DD \u05DE\u05D7\u05D5\u05D5\u05DF \u05E6\u05D1\u05E2\u05D5\u05E0\u05D9. \u05DC\u05D7\u05E6\u05D5 \u05DE\u05D7\u05D5\u05E5 \u05D0\u05D5 Enter \u05DC\u05E9\u05DE\u05D9\u05E8\u05D4.",
      },
      {
        q: "\u05D1\u05E0\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD \u05D5\u05D2\u05E8\u05D9\u05E8\u05D4",
        a: "\u05E4\u05EA\u05D7\u05D5 \u05D0\u05EA \u05D1\u05E0\u05E7 \u05D4\u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD (\u05E6\u05D3 \u05E9\u05DE\u05D0\u05DC \u05E2\u05DC\u05D9\u05D5\u05DF \u05D1\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4). \u05D2\u05E8\u05E8\u05D5 \u05D0\u05DC\u05DE\u05E0\u05D8 \u05DE\u05D4\u05E8\u05E9\u05D9\u05DE\u05D4 \u05D5\u05E9\u05D7\u05E8\u05E8\u05D5 \u05D0\u05D5\u05EA\u05D5 \u05D1\u05D0\u05D6\u05D5\u05E8 \u05D4\u05DE\u05E1\u05D5\u05DE\u05DF \u05E9\u05DE\u05D5\u05E4\u05D9\u05E2 \u05D1\u05E2\u05DE\u05D5\u05D3.",
      },
      {
        q: "\u05D4\u05D5\u05E1\u05E4\u05EA \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD \u05D1\u05EA\u05D5\u05DA \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD",
        a: "\u05D0\u05D7\u05E8\u05D9 \u05E9\u05D2\u05E8\u05E8\u05EA\u05DD \u05E1\u05E7\u05E9\u05DF \u05DC\u05E2\u05DE\u05D5\u05D3, \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D2\u05E8\u05D5\u05E8 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD \u05E0\u05D5\u05E1\u05E4\u05D9\u05DD \u05DC\u05EA\u05D5\u05DB\u05D5 \u2014 \u05D4\u05D0\u05D6\u05D5\u05E8\u05D9\u05DD \u05D4\u05DE\u05E1\u05D5\u05DE\u05E0\u05D9\u05DD \u05D9\u05D5\u05E4\u05D9\u05E2\u05D5 \u05D1\u05D6\u05DE\u05DF \u05D2\u05E8\u05D9\u05E8\u05D4.",
      },
      {
        q: "\u05E2\u05D5\u05D6\u05E8 AI",
        a: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4-AI (\u05D4\u05DB\u05EA\u05D5\u05DD \u05E2\u05DD \u05D4\u05E0\u05D9\u05E6\u05D5\u05E5) \u05DB\u05D3\u05D9 \u05DC\u05D9\u05D9\u05E6\u05E8 \u05EA\u05D5\u05DB\u05DF \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9 \u05DC\u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05D1\u05D0\u05EA\u05E8.",
      },
    ],
  },
  {
    id: "nav",
    icon: <Layers className="w-4 h-4" />,
    title: "\u05E0\u05D9\u05D5\u05D5\u05D8 \u05D5\u05E2\u05DE\u05D5\u05D3\u05D9\u05DD",
    items: [
      {
        q: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05EA\u05E4\u05E8\u05D9\u05D8",
        a: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05EA\u05E4\u05E8\u05D9\u05D8 (\u05E1\u05D2\u05D5\u05DC, \u05D1\u05E1\u05E8\u05D2\u05DC \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4). \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3/\u05DC\u05DE\u05D7\u05D5\u05E7/\u05DC\u05E9\u05E0\u05D5\u05EA \u05E9\u05DD/\u05DC\u05E1\u05D3\u05E8 \u05DE\u05D7\u05D3\u05E9 \u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD \u05D5\u05EA\u05EA\u05D9-\u05E2\u05DE\u05D5\u05D3\u05D9\u05DD.",
      },
      {
        q: "\u05E2\u05E5 \u05D0\u05EA\u05E8",
        a: "\u05D2\u05E9\u05D5 \u05DC\u05E2\u05DE\u05D5\u05D3 \u05E2\u05E5 \u05D0\u05EA\u05E8 (\u05DE\u05DC\u05D5\u05D7 \u05D4\u05D1\u05E7\u05E8\u05D4 > \u05E2\u05E5 \u05D0\u05EA\u05E8) \u05DC\u05E8\u05D0\u05D5\u05EA \u05EA\u05E6\u05D5\u05D2\u05EA Kanban \u05E9\u05DC \u05DB\u05DC \u05D4\u05E2\u05DE\u05D5\u05D3\u05D9\u05DD \u05D1\u05D0\u05EA\u05E8. \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D2\u05E8\u05D5\u05E8, \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05D5\u05DC\u05D1\u05D7\u05D5\u05E8 \u05E1\u05D5\u05D2 \u05E2\u05DE\u05D5\u05D3.",
      },
      {
        q: "\u05E1\u05D5\u05D2\u05D9 \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD",
        a: "\u05DC\u05DB\u05DC \u05E2\u05DE\u05D5\u05D3 \u05D3\u05D9\u05E0\u05DE\u05D9 \u05D9\u05E9 3 \u05E1\u05D5\u05D2\u05D9\u05DD: \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 (\u05D2\u05E8\u05D9\u05D3 \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05D9\u05DC\u05D3\u05D9\u05DD), \u05EA\u05EA-\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 (\u05DC\u05D9\u05D9\u05D0\u05D0\u05D5\u05D8 \u05DE\u05E4\u05D5\u05E8\u05D8 \u05E2\u05DD \u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA/FAQ), \u05D5\u05D4\u05EA\u05D0\u05DE\u05D4 \u05D0\u05D9\u05E9\u05D9\u05EA (\u05E2\u05DE\u05D5\u05D3 \u05E8\u05D9\u05E7 \u05DC\u05D1\u05E0\u05D9\u05D9\u05D4 \u05D7\u05D5\u05E4\u05E9\u05D9\u05EA).",
      },
    ],
  },
  {
    id: "seo",
    icon: <Search className="w-4 h-4" />,
    title: "SEO",
    items: [
      {
        q: "\u05E0\u05D9\u05D4\u05D5\u05DC SEO",
        a: "\u05D2\u05E9\u05D5 \u05DC\u05E2\u05DE\u05D5\u05D3 \u05E0\u05D9\u05D4\u05D5\u05DC SEO (\u05DE\u05DC\u05D5\u05D7 \u05D4\u05D1\u05E7\u05E8\u05D4 > SEO) \u05DC\u05E2\u05E8\u05D5\u05DA \u05DB\u05D5\u05EA\u05E8\u05D5\u05EA, \u05EA\u05D9\u05D0\u05D5\u05E8\u05D9\u05DD \u05D5\u05DE\u05D9\u05DC\u05D5\u05EA \u05DE\u05E4\u05EA\u05D7 \u05DC\u05DB\u05DC \u05E2\u05DE\u05D5\u05D3 \u05D1\u05D0\u05EA\u05E8.",
      },
      {
        q: "\u05DE\u05D9\u05DC\u05D5\u05EA \u05DE\u05E4\u05EA\u05D7",
        a: "\u05D1\u05D8\u05D1\u05DC\u05EA \u05D4-SEO \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05DE\u05D9\u05DC\u05D5\u05EA \u05DE\u05E4\u05EA\u05D7 \u05DE\u05E8\u05D5\u05D1\u05D5\u05EA \u05DC\u05DB\u05DC \u05E2\u05DE\u05D5\u05D3, \u05DE\u05D5\u05E4\u05E8\u05D3\u05D5\u05EA \u05D1\u05E4\u05E1\u05D9\u05E7\u05D9\u05DD.",
      },
    ],
  },
  {
    id: "store",
    icon: <ShoppingCart className="w-4 h-4" />,
    title: "\u05D7\u05E0\u05D5\u05EA \u05D0\u05D5\u05E0\u05DC\u05D9\u05D9\u05DF",
    items: [
      {
        q: "\u05DE\u05E6\u05D1 \u05D7\u05E0\u05D5\u05EA \u05DE\u05D5\u05DC \u05DE\u05D5\u05DC \u05DE\u05E6\u05D1 \u05E7\u05D8\u05DC\u05D5\u05D2",
        a: "\u05D1\u05E1\u05E8\u05D2\u05DC \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4 \u05D9\u05E9 \u05DB\u05E4\u05EA\u05D5\u05E8 \u05DC\u05D4\u05D7\u05DC\u05E4\u05D4 \u05D1\u05D9\u05DF \u05DE\u05E6\u05D1 \u05D7\u05E0\u05D5\u05EA (\u05E2\u05DD \u05E2\u05D2\u05DC\u05D4 \u05D5\u05DE\u05D7\u05D9\u05E8\u05D9\u05DD) \u05DC\u05DE\u05E6\u05D1 \u05E7\u05D8\u05DC\u05D5\u05D2 (\u05DC\u05DC\u05D0 \u05E7\u05E0\u05D9\u05D9\u05D4 \u05D0\u05D5\u05E0\u05DC\u05D9\u05D9\u05DF).",
      },
      {
        q: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05D5\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA",
        a: "\u05E2\u05E8\u05DB\u05D5 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD, \u05DE\u05D7\u05D9\u05E8\u05D9\u05DD \u05D5\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u05D9\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4. \u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05DC \u05E9\u05D3\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05E9\u05E0\u05D5\u05EA \u05D0\u05D5\u05EA\u05D5.",
      },
    ],
  },
  {
    id: "comments",
    icon: <BookOpen className="w-4 h-4" />,
    title: "\u05D4\u05E2\u05E8\u05D5\u05EA \u05D5\u05E9\u05D9\u05EA\u05D5\u05E3 \u05E4\u05E2\u05D5\u05DC\u05D4",
    items: [
      {
        q: "\u05D4\u05D5\u05E1\u05E4\u05EA \u05D4\u05E2\u05E8\u05D5\u05EA",
        a: "\u05D4\u05E4\u05E2\u05D9\u05DC\u05D5 \u05DE\u05E6\u05D1 \u05D4\u05E2\u05E8\u05D5\u05EA (\u05D0\u05D9\u05E7\u05D5\u05DF \u05D1\u05D5\u05E2\u05D4 \u05D1\u05E6\u05D3 \u05E9\u05DE\u05D0\u05DC) \u05D5\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05DC \u05DE\u05E7\u05D5\u05DD \u05D1\u05E2\u05DE\u05D5\u05D3 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05E6\u05DE\u05D9\u05D3 \u05E4\u05D9\u05DF. \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D2\u05E8\u05D5\u05E8 \u05E4\u05D9\u05E0\u05D9\u05DD \u05D1\u05D3\u05E8\u05D0\u05D2.",
      },
      {
        q: "\u05EA\u05D2\u05D5\u05D1\u05D5\u05EA \u05DE\u05E7\u05D5\u05E0\u05E0\u05D5\u05EA",
        a: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05D2\u05D1 \u05DE\u05EA\u05D7\u05EA \u05DB\u05DC \u05D4\u05E2\u05E8\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05EA\u05D2\u05D5\u05D1\u05D4. \u05D4\u05E7\u05DC\u05D9\u05D3\u05D5 Ctrl+Enter \u05DC\u05E9\u05DC\u05D9\u05D7\u05D4, Esc \u05DC\u05D1\u05D9\u05D8\u05D5\u05DC. \u05DE\u05E1\u05E4\u05E8 \u05D4\u05EA\u05D2\u05D5\u05D1\u05D5\u05EA \u05DE\u05D5\u05E4\u05D9\u05E2 \u05DB\u05D1\u05D0\u05D3\u05D2\u05F3 \u05E2\u05DC \u05D4\u05E4\u05D9\u05DF.",
      },
      {
        q: "\u05EA\u05D9\u05D5\u05D2 \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD (@)",
        a: "\u05D4\u05E7\u05DC\u05D9\u05D3\u05D5 @ \u05D5\u05E9\u05DD \u05D4\u05DE\u05E9\u05EA\u05DE\u05E9 \u05DB\u05D3\u05D9 \u05DC\u05EA\u05D9\u05D9\u05D2 \u05D0\u05D5\u05EA\u05D5. \u05D4\u05DE\u05EA\u05D5\u05D9\u05D9\u05D2 \u05D9\u05E7\u05D1\u05DC \u05D4\u05EA\u05E8\u05D0\u05D4 \u05D1\u05D0\u05E4\u05DC\u05D9\u05E7\u05E6\u05D9\u05D4 \u05D5\u05D1\u05DE\u05D9\u05D9\u05DC.",
      },
      {
        q: "\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D1\u05D6\u05DE\u05DF \u05D0\u05DE\u05EA",
        a: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05D0\u05D9\u05E7\u05D5\u05DF \u05D4\u05E4\u05E2\u05DE\u05D5\u05DF \u05DB\u05D3\u05D9 \u05DC\u05E8\u05D0\u05D5\u05EA \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D7\u05D3\u05E9\u05D5\u05EA. \u05DC\u05D7\u05D9\u05E6\u05D4 \u05E2\u05DC \u05D4\u05EA\u05E8\u05D0\u05D4 \u05DE\u05E0\u05D5\u05D5\u05D8\u05EA \u05D9\u05E9\u05D9\u05E8\u05D5\u05EA \u05DC\u05D4\u05E2\u05E8\u05D4 \u05D4\u05E8\u05DC\u05D5\u05D5\u05E0\u05D8\u05D9\u05EA \u05E2\u05DD \u05D4\u05D3\u05D2\u05E9\u05D4 \u05E6\u05D4\u05D5\u05D1\u05D4.",
      },
    ],
  },
  {
    id: "shortcuts",
    icon: <Keyboard className="w-4 h-4" />,
    title: "\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05DC\u05D3\u05EA",
    items: [
      { q: "Ctrl + E", a: "\u05DB\u05E0\u05D9\u05E1\u05D4/\u05D9\u05E6\u05D9\u05D0\u05D4 \u05DE\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4" },
      { q: "Ctrl + Z", a: "\u05D1\u05D9\u05D8\u05D5\u05DC \u05E9\u05D9\u05E0\u05D5\u05D9 \u05D0\u05D7\u05E8\u05D5\u05DF" },
      { q: "Ctrl + Y", a: "\u05E9\u05D7\u05D6\u05D5\u05E8 \u05E9\u05D9\u05E0\u05D5\u05D9" },
      { q: "Ctrl + S", a: "\u05E9\u05DE\u05D9\u05E8\u05D4 \u05DE\u05D4\u05D9\u05E8\u05D4" },
      { q: "Ctrl + Shift + P", a: "\u05DE\u05E6\u05D1 \u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4" },
      { q: "Ctrl + K", a: "\u05E4\u05EA\u05D9\u05D7\u05EA \u05E4\u05DC\u05D8\u05EA \u05E4\u05E7\u05D5\u05D3\u05D5\u05EA (Command Palette)" },
      { q: "Escape", a: "\u05D9\u05E6\u05D9\u05D0\u05D4 \u05DE\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4/\u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4" },
    ],
  },
];

/* ---------- Accordion section ---------- */

function HelpAccordion({ section }: { section: HelpSection }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${btnReset} w-full flex items-center justify-between px-4 py-3 text-right hover:bg-gray-50`}
        style={{ fontWeight: 600 }}
      >
        <div className="flex items-center gap-2.5">
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: "#eef2ff", color: "#4f46e5" }}
          >
            {section.icon}
          </span>
          <span className="text-sm text-gray-800">{section.title}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="overflow-hidden">
          <div className="px-4 pb-3 space-y-3 border-t border-gray-100 pt-3">
            {section.id === "shortcuts"
              ? section.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <kbd
                      className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-700 font-mono whitespace-nowrap"
                      style={{ fontWeight: 600 }}
                    >
                      {item.q}
                    </kbd>
                    <span className="text-xs text-gray-500 text-left flex-1">
                      {item.a}
                    </span>
                  </div>
                ))
              : section.items.map((item, i) => (
                  <div key={i}>
                    <p
                      className="text-xs text-gray-800 mb-0.5"
                      style={{ fontWeight: 600 }}
                    >
                      {item.q}
                    </p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Main HelpDrawer — button on bottom-RIGHT, drawer slides from RIGHT
   ================================================================ */

export function HelpDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const autoStartAttempted = useRef(false);
  const auth = useAuthSafe();
  const userRole = auth?.user?.role ?? "admin";

  // Auto-start tour for first-time users (only once per mount)
  useEffect(() => {
    if (autoStartAttempted.current) return;
    autoStartAttempted.current = true;

    if (!isTourCompleted()) {
      // Delay so the page renders fully before the tour starts
      const timer = setTimeout(() => setTourOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen]);

  // Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  const startTour = () => {
    setIsOpen(false);
    // Small delay so the drawer closes before the tour starts
    setTimeout(() => setTourOpen(true), 200);
  };

  return (
    <>
      {/* Floating ? button — bottom-RIGHT corner */}
      <button
        type="button"
        id="help-drawer-btn"
        data-tour-step="help-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`${btnReset} fixed z-[9997] rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110`}
        style={{
          bottom: 24,
          right: 24,
          width: 44,
          height: 44,
          background: isOpen
            ? "linear-gradient(135deg, #059669, #10b981)"
            : "linear-gradient(135deg, #4f46e5, #7c3aed)",
          color: "#fff",
        }}
        title={"\u05E2\u05D6\u05E8\u05D4 \u05D5\u05DE\u05D3\u05E8\u05D9\u05DA"}
      >
        {isOpen ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Drawer panel — slides from RIGHT */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed top-0 right-0 bottom-0 z-[9996] w-[340px] bg-white border-l border-gray-200 shadow-2xl flex flex-col"
          dir="rtl"
          style={{ animation: "helpSlideInRight 0.25s ease-out" }}
        >
          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between shrink-0"
            style={{
              background: "linear-gradient(135deg, #031d4e 0%, #0a2d6b 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="flex items-center gap-2.5">
              <BookOpen className="w-5 h-5" style={{ color: "#fbbf24" }} />
              <span
                className="text-white text-base"
                style={{ fontWeight: 700 }}
              >
                {"\u05E2\u05D6\u05E8\u05D4 \u05D5\u05DE\u05D3\u05E8\u05D9\u05DA"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`${btnReset} text-white/60 hover:text-white`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {/* Tour button */}
            <StartTourButton onClick={startTour} />

            {/* Quick tip */}
            <div
              className="rounded-xl px-4 py-3 flex items-start gap-2.5"
              style={{ background: "#fef9c3", border: "1px solid #fde68a" }}
            >
              <Lightbulb
                className="w-4 h-4 shrink-0 mt-0.5"
                style={{ color: "#d97706" }}
              />
              <p className="text-xs text-amber-800 leading-relaxed">
                {"\u05D8\u05D9\u05E4: \u05D4\u05E9\u05EA\u05DE\u05E9\u05D5 \u05D1-"}
                <kbd
                  className="px-1.5 py-0.5 text-[10px] bg-amber-200/60 rounded font-mono mx-0.5"
                  style={{ fontWeight: 700 }}
                >
                  Ctrl+K
                </kbd>
                {" \u05DC\u05E4\u05EA\u05D9\u05D7\u05D4 \u05DE\u05D4\u05D9\u05E8\u05D4 \u05E9\u05DC \u05E4\u05DC\u05D8\u05EA \u05E4\u05E7\u05D5\u05D3\u05D5\u05EA \u05E2\u05DD \u05D7\u05D9\u05E4\u05D5\u05E9 \u05D5\u05E0\u05D9\u05D5\u05D5\u05D8 \u05DE\u05D4\u05D9\u05E8."}
              </p>
            </div>

            {/* Sections */}
            {HELP_SECTIONS.map((section) => (
              <HelpAccordion key={section.id} section={section} />
            ))}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-3 border-t border-gray-200 text-center shrink-0"
            style={{ background: "#f9fafb" }}
          >
            <p className="text-[11px] text-gray-400">
              {"ZAP WEB \u2014 \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4"}
            </p>
          </div>
        </div>
      )}

      {/* Backdrop when drawer is open */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9995]"
          style={{ background: "rgba(0,0,0,0.3)" }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Tour overlay */}
      <OnboardingTour isOpen={tourOpen} onClose={() => setTourOpen(false)} userRole={userRole} />

      {/* Keyframe for slide-in from right */}
      <style>{`
        @keyframes helpSlideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}